import React, { useState } from 'react';
import { Bell, AlertCircle, Send, ChevronRight, ChevronLeft, X, CheckCircle } from 'lucide-react';

const ActionRequiredPanel = ({ actionItems, setActionItems, expanded, setExpanded, onSuccess }) => {
  const handleActionInstruction = (itemId, instruction) => {
    if (!instruction.trim()) {
      alert('Please provide instructions for the AI');
      return;
    }

    // Remove the resolved item
    setActionItems(prev => prev.filter(item => item.id !== itemId));
    
    onSuccess(`AI instruction received: "${instruction}"`);
    
    // Clear the textarea
    const textarea = document.getElementById(`instruction-${itemId}`);
    if (textarea) textarea.value = '';
  };

  const hasActionItems = actionItems && actionItems.length > 0;

  return (
    <div style={{
      position: 'fixed',
      right: 0,
      top: 0,
      bottom: 0,
      width: expanded ? '400px' : '80px',
      background: 'linear-gradient(180deg, #0f1d35 0%, #0A1628 100%)',
      borderLeft: `1px solid ${hasActionItems ? 'rgba(251, 191, 36, 0.3)' : 'rgba(0, 212, 170, 0.1)'}`,
      transition: 'width 0.3s ease, border-color 0.3s ease',
      zIndex: 200,
      display: 'flex',
      flexDirection: 'column',
      boxShadow: expanded ? '-4px 0 20px rgba(0, 0, 0, 0.3)' : 'none'
    }}>
      {/* Trigger/Header */}
      <div
        onClick={() => setExpanded(!expanded)}
        style={{
          padding: expanded ? '24px 20px' : '24px 16px',
          cursor: 'pointer',
          display: 'flex',
          flexDirection: expanded ? 'row' : 'column',
          alignItems: 'center',
          gap: '12px',
          borderBottom: `1px solid ${hasActionItems ? 'rgba(251, 191, 36, 0.2)' : 'rgba(0, 212, 170, 0.1)'}`,
          background: hasActionItems ? 'rgba(251, 191, 36, 0.05)' : 'transparent',
          transition: 'all 0.3s ease'
        }}
      >
        <div style={{
          width: '40px',
          height: '40px',
          borderRadius: '50%',
          background: hasActionItems 
            ? 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)'
            : 'rgba(0, 212, 170, 0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          flexShrink: 0
        }}>
          {hasActionItems ? (
            <>
              <AlertCircle size={20} color="#78350f" />
              <div style={{
                position: 'absolute',
                top: '-4px',
                right: '-4px',
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                background: '#ef4444',
                color: '#FFFFFF',
                fontSize: '11px',
                fontWeight: '700',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '2px solid #0A1628'
              }}>
                {actionItems.length}
              </div>
            </>
          ) : (
            <CheckCircle size={20} color="#00D4AA" />
          )}
        </div>

        {expanded && (
          <>
            <div style={{ flex: 1 }}>
              <div style={{ 
                fontSize: '16px', 
                fontWeight: '700', 
                color: hasActionItems ? '#fbbf24' : '#00D4AA',
                marginBottom: '2px'
              }}>
                {hasActionItems ? 'Action Required' : 'All Clear'}
              </div>
              <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.6)' }}>
                {hasActionItems ? `${actionItems.length} item${actionItems.length !== 1 ? 's' : ''}` : 'No issues'}
              </div>
            </div>
            <ChevronRight size={20} color="rgba(255, 255, 255, 0.5)" />
          </>
        )}

        {!expanded && (
          <ChevronLeft size={16} color="rgba(255, 255, 255, 0.5)" />
        )}
      </div>

      {/* Content Area */}
      {expanded && (
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '20px'
        }}>
          {!hasActionItems ? (
            <div style={{
              textAlign: 'center',
              padding: '60px 20px',
              color: 'rgba(255, 255, 255, 0.5)'
            }}>
              <CheckCircle size={48} style={{ marginBottom: '16px', opacity: 0.5, margin: '0 auto 16px' }} />
              <p style={{ fontSize: '15px', fontWeight: '600', marginBottom: '8px' }}>
                No Actions Required
              </p>
              <p style={{ fontSize: '13px', lineHeight: '1.5' }}>
                All systems running smoothly.<br/>AI routing is operating normally.
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {actionItems.map(item => (
                <div
                  key={item.id}
                  style={{
                    background: 'rgba(251, 191, 36, 0.05)',
                    border: '2px solid rgba(251, 191, 36, 0.2)',
                    borderRadius: '12px',
                    padding: '16px',
                    transition: 'all 0.3s ease'
                  }}
                >
                  {/* Item Header */}
                  <div style={{ display: 'flex', alignItems: 'start', gap: '12px', marginBottom: '12px' }}>
                    <div style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      background: 'rgba(251, 191, 36, 0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <AlertCircle size={18} color="#fbbf24" />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '14px', fontWeight: '700', color: '#FFFFFF', marginBottom: '4px' }}>
                        {item.title}
                      </div>
                      <div style={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.7)', lineHeight: '1.4' }}>
                        {item.description}
                      </div>
                    </div>
                  </div>

                  {/* Instruction Box */}
                  <div style={{
                    background: 'rgba(0, 0, 0, 0.3)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '8px',
                    padding: '12px'
                  }}>
                    <label style={{
                      display: 'block',
                      fontSize: '12px',
                      fontWeight: '600',
                      color: 'rgba(255, 255, 255, 0.8)',
                      marginBottom: '8px'
                    }}>
                      Tell AI what to do:
                    </label>
                    <textarea
                      id={`instruction-${item.id}`}
                      placeholder="e.g., 'Route to Sarah, she handles special orders' or 'Try again in 30 minutes'"
                      style={{
                        width: '100%',
                        background: 'rgba(255, 255, 255, 0.05)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '6px',
                        padding: '10px 12px',
                        color: '#FFFFFF',
                        fontSize: '13px',
                        minHeight: '70px',
                        resize: 'vertical',
                        marginBottom: '10px',
                        fontFamily: 'inherit'
                      }}
                    />
                    <button
                      onClick={() => {
                        const instruction = document.getElementById(`instruction-${item.id}`).value;
                        if (instruction.trim()) {
                          handleActionInstruction(item.id, instruction);
                        }
                      }}
                      style={{
                        width: '100%',
                        background: 'linear-gradient(135deg, #00D4AA 0%, #00A88A 100%)',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '10px 16px',
                        color: '#FFFFFF',
                        fontSize: '13px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-1px)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 212, 170, 0.3)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      <Send size={14} />
                      Submit Instruction
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ActionRequiredPanel;